import DevTypes from "@wesstron/utils/Api/constants/devTypes";
import RoleTypes from "@wesstron/utils/Api/constants/roleTypes";
import {get} from "lodash";
import moment from "moment";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useLocation} from "react-router-dom/cjs/react-router-dom";
import {formValueSelector} from "redux-form";
import {feedingDestroy, feedingInit} from "../../../../actions/feedingActions";
import {getPigHistory} from "../../../../actions/rfidHistoryActions";
import {
    CurveDayShowingType,
    FeedingType,
} from "../../../../constans/feedingTypes";
import {Level} from "../../../../constans/levelTypes";
import buildingsDB from "../../../../database/buildingsDB";
import devicesDB from "../../../../database/devicesDB";
import {getFakeDataHistoryModal} from "../../../../demo/feeding/fakeFunctions";
import NewIOT from "../../../../IOT/NewIOT";
import {makeGetAggregatedData} from "../../../../selectors/aggregatedSelector";
import {makeGetBuildingsMap} from "../../../../selectors/buildingsSelector";
import {makeGetFeedingSorted} from "../../../../selectors/feedingSelector";
import {makeGetRFIDPigHistory} from "../../../../selectors/rfidHistorySelector";
import {isUsingFakeData} from "../../../../utils/DemoUtils";
import {isInFeedingTab} from "../../../../utils/DispenserNRFUtils";
import {
    getCurveDayShowingType,
    getSelectedDispensers,
} from "../../../../utils/FeedingUtils";
import {checkIfUserHasRole} from "../../../../utils/NewRolesUtils";
import AggFetcher from "../../../agg-fetcher/AggFetcher";
import CollapsableContainer from "../../../animal-documents/containers/CollapsableContainer";
import LoadingComponent from "../../../loading/LoadingComponent";
import {getDataExtended} from "../../../modals-new/feeding-history/FeedingHistoryModal";
import {FeedingManageFeedingFormConnected} from "../../../modals-new/feeding-manage-feeding/FeedingManageFeedingModal";

const ModalName = "feeding-manage-feeding-modal";

export const SowFeedingCard = ({animal}) => {
    const [loading, setLoading] = useState(true);
    const [isReadonly, setIsReadonly] = useState(true);
    const {t} = useTranslation();
    const loc = useLocation();
    const isOnFeedingLink = useMemo(
        () => loc.pathname.includes("feeding"),
        [loc]
    );
    const demo = useMemo(() => isUsingFakeData(), []);

    useEffect(() => {
        setLoading(true);
        setIsReadonly(true);
        const timer = setTimeout(() => setLoading(false), 4000);
        return () => clearTimeout(timer);
    }, [animal.AnmID]);

    const devices = useMemo(() => {
        return devicesDB
            .getDevicesInPlcmntID(animal.PlcmntID)
            .filter((device) => {
                if (device.DevType === DevTypes.DISPENSER) return true;
                return !!(
                    device.DevType === DevTypes.DISPENSER_NRF &&
                    isInFeedingTab(device)
                );
            });
    }, [animal.PlcmntID]);

    const getBuildingsMap = useMemo(makeGetBuildingsMap, []);
    const buildingsMap = useSelector(getBuildingsMap);

    const dispatch = useDispatch();
    const location = useMemo(
        () => buildingsMap.get(animal.PlcmntID),
        [buildingsMap, animal.PlcmntID]
    );

    const cID = useMemo(() => {
        if (!location) return null;
        if (location.level === Level.BOX) return location.parentId;
        return location.id;
    }, [location]);

    const chamber = useMemo(() => buildingsDB.getLocationByID(cID), [cID]);

    const chamberParams = useMemo(
        () => ({...chamber, animal: animal.AnmID}),
        [chamber, animal.AnmID]
    );
    const getFeedingSorted = useMemo(() => makeGetFeedingSorted(), []);
    const dispensersSelected = useSelector((state) =>
        getFeedingSorted(state, chamberParams)
    );

    const formSelector = useMemo(() => formValueSelector(ModalName), []);
    const curve = useSelector((state) => formSelector(state, "curve"));
    const curveDay = useSelector((state) => formSelector(state, "curveDay"));
    const repetitionDate = useSelector((state) =>
        formSelector(state, "repetitionDate")
    );

    // Obliczanie zakresu dat na podstawie krzywej
    const params = useMemo(() => {
        if (!curve) {
            // Domyślny zakres jeśli nie ma krzywej
            return {
                AggDataTimeFrom: +moment
                    .utc()
                    .startOf("day")
                    .subtract(30, "days"),
                AggDataTimeTo: +moment.utc().startOf("day"),
            };
        }

        const showingType = getCurveDayShowingType(curve);
        const curveLen = get(curve, "SetData.Days.length", 0);
        const repetitionDay = get(curve, "SetData.InseminationJumpTo", 0);
        let startDate;

        switch (showingType) {
            case CurveDayShowingType.BOTH:
                // Od daty inseminacji minus dni powtórzenia do teraz
                if (repetitionDate) {
                    startDate = moment(repetitionDate, "YYYY-MM-DD")
                        .subtract(repetitionDay, "day")
                        .startOf("day");
                } else {
                    startDate = moment()
                        .startOf("day")
                        .subtract(curveDay + repetitionDay - 1, "day");
                }
                break;
            case CurveDayShowingType.AFTER:
                // Od daty rozpoczęcia do teraz
                if (repetitionDate) {
                    startDate = moment(repetitionDate, "YYYY-MM-DD").startOf(
                        "day"
                    );
                } else {
                    startDate = moment()
                        .startOf("day")
                        .subtract(curveDay - 1, "day");
                }
                break;
            case CurveDayShowingType.BEFORE:
                // Od daty minus długość krzywej do teraz
                if (repetitionDate) {
                    startDate = moment(repetitionDate, "YYYY-MM-DD")
                        .subtract(curveLen, "day")
                        .startOf("day");
                } else {
                    startDate = moment()
                        .startOf("day")
                        .subtract(curveDay + curveLen - 1, "day");
                }
                break;
            case CurveDayShowingType.NORMAL:
            default:
                // Od początku krzywej (dzień 1) do teraz
                const calculatedCurveDay = isFinite(curveDay)
                    ? Math.max(Math.min(curveDay, curveLen || 1), 1)
                    : 1;
                startDate = moment()
                    .startOf("day")
                    .subtract(calculatedCurveDay - 1, "day");
                break;
        }

        // Cofamy o 1 dzień, aby obejmował również początek dnia 1
        const dataStartDate = moment(startDate)
            .subtract(1, "day")
            .startOf("day");

        return {
            AggDataTimeFrom: +dataStartDate.valueOf(),
            AggDataTimeTo: +moment.utc().startOf("day").valueOf(),
        };
    }, [curve, curveDay, repetitionDate]);

    const startSendingDeviceState = useCallback(
        (deviceIds, gatewayId, chamber, {keepAlive = false} = {}) => {
            const {CID, IndividualFeeding} = chamber;
            NewIOT.startSendingFeedingState(deviceIds, {keepAlive});
            if (!IndividualFeeding) {
                NewIOT.startSendingFeedingStateRFID(gatewayId, CID, {
                    keepAlive: true,
                });
            }
        },
        []
    );

    const startPolling = useCallback(() => {
        if (devices.length) {
            let gatewayWithDevices = new Map();
            for (let device of devices) {
                let gatewayId = device.GatewayID;
                if (gatewayId) {
                    let deviceIds = gatewayWithDevices.get(gatewayId) || [];
                    deviceIds.push(device.DevID);
                    gatewayWithDevices.set(gatewayId, deviceIds);
                }
            }
            for (let [gatewayId, deviceIds] of gatewayWithDevices.entries()) {
                startSendingDeviceState(deviceIds, gatewayId, chamber, {
                    keepAlive: false,
                });
            }
        }
    }, [devices, chamber, startSendingDeviceState]);

    useEffect(() => {
        if (!isOnFeedingLink && cID && devices.length) {
            dispatch(
                feedingInit(chamber, {dispensers: devices, animals: [animal]})
            );
            startPolling();
            return () => dispatch(feedingDestroy(devices));
        }
    }, [
        cID,
        animal,
        devices,
        chamber,
        dispatch,
        isOnFeedingLink,
        startPolling,
    ]);

    const {feedingType, selectedNodes, receivers, chamberID, sectorType} =
        useMemo(() => {
            if (cID && dispensersSelected?.length)
                return getSelectedDispensers(cID, dispensersSelected);
            return {};
        }, [cID, dispensersSelected]);

    const getAggregated = useMemo(() => makeGetAggregatedData(), []);
    const getRFIDPigHistory = useMemo(() => makeGetRFIDPigHistory(), []);

    const _animalDataKey = useMemo(
        () => ({
            RFID: get(selectedNodes, "[0].RFID"),
            AnmID: get(selectedNodes, "[0].animal.AnmID"),
        }),
        [selectedNodes]
    );

    const _deviceDataKey = useMemo(
        () => ({
            device: get(selectedNodes, "[0].device"),
            DevID: get(selectedNodes, "[0].receiver.deviceId"),
            GwID: get(selectedNodes, "[0].receiver.gatewayId"),
            index: get(selectedNodes, "[0].receiver.index"),
        }),
        [selectedNodes]
    );

    useEffect(() => {
        if (
            !demo &&
            feedingType === FeedingType.GROUP &&
            selectedNodes?.length
        ) {
            dispatch(getPigHistory(_animalDataKey));
        }
    }, []); //eslint-disable-line

    const aggregated = useSelector((state) =>
        feedingType === FeedingType.INDIVIDUAL
            ? getAggregated(state, _deviceDataKey)
            : getRFIDPigHistory(state, _animalDataKey)
    );

    // Główna funkcja pobierająca dane do karmienia
    const chartData = useMemo(() => {
        if (demo) return getFakeDataHistoryModal(selectedNodes);
        return getDataExtended(
            aggregated.data,
            feedingType,
            _animalDataKey,
            params
        );
    }, [
        demo,
        selectedNodes,
        aggregated.data,
        feedingType,
        params,
        _animalDataKey,
    ]);
    const canView = checkIfUserHasRole(RoleTypes.DEVICE_FEEDING_READ);

    if (!cID || !dispensersSelected || !selectedNodes?.length || !canView)
        return null;

    return (
        <CollapsableContainer.Card
            id="cy-feeding-card"
            header={t("feeding")}
            defaultExpanded={true}>
            {!demo &&
                feedingType === FeedingType.INDIVIDUAL &&
                _deviceDataKey.device && (
                    <AggFetcher
                        device={_deviceDataKey.device}
                        showNewestDate={false}
                        key={`${_deviceDataKey.DevID}_${_deviceDataKey.index}`}
                        start={params.AggDataTimeFrom}
                        end={params.AggDataTimeTo}
                        index={_deviceDataKey.index}
                        isDuration={true}
                    />
                )}
            {loading ? (
                <LoadingComponent
                    style={{position: "relative"}}
                    isLoading
                    type="component"
                />
            ) : (
                <FeedingManageFeedingFormConnected
                    type={feedingType}
                    selectedNodes={selectedNodes}
                    receivers={receivers}
                    locationID={chamberID}
                    sectorType={sectorType}
                    fullscreen={false}
                    readOnly={isReadonly}
                    setIsReadonly={setIsReadonly}
                    showSubmitButton
                    feedingChartData={chartData}
                />
            )}
        </CollapsableContainer.Card>
    );
};
